<template>
  <div class="rb-filter-toolbar">
    <div class="toolbar-main">
      <div class="toolbar-left">
        <div class="search-wrapper">
          <v-menu
            v-model="searchMenu"
            location="bottom start"
            :offset="6"
            content-class="rb-toolbar-popup"
          >
            <template #activator="{ props }">
              <button
                v-bind="props"
                type="button"
                class="search-type-selector"
                :aria-expanded="searchMenu"
              >
                <span class="search-type-label">
                  {{ selectedSearchField.label || "Search By" }}
                </span>
                <v-icon size="17">
                  {{ searchMenu ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </button>
            </template>

            <v-list
              class="toolbar-menu search-menu"
              density="compact"
              elevation="5"
              rounded="lg"
            >
              <div class="menu-heading">Search By</div>
              <v-divider class="menu-divider" />

              <v-list-item
                v-for="option in availableSearchOptions"
                :key="option.value"
                class="menu-option search-option"
                :class="{
                  'search-option-selected': isSearchSelected(option),
                }"
                :aria-selected="isSearchSelected(option)"
                role="option"
                @click="selectSearchField(option)"
              >
                <template #prepend>
                  <span
                    class="search-radio"
                    :class="{
                      'search-radio-checked': isSearchSelected(option),
                    }"
                  >
                    <span v-if="isSearchSelected(option)"></span>
                  </span>
                </template>

                <v-list-item-title>
                  {{ option.label }}
                </v-list-item-title>

                <template v-if="isSearchSelected(option)" #append>
                  <v-icon size="16" class="selected-check"> mdi-check </v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-text-field
            v-model="searchValue"
            :placeholder="`Search by ${
              selectedSearchField.label || 'keyword'
            }...`"
            variant="plain"
            density="compact"
            hide-details
            clearable
            class="search-input"
            @keyup.enter="applyFilters"
            @click:clear="handleSearchClear"
          />

          <v-btn
            class="search-btn"
            color="primary"
            variant="flat"
            aria-label="Search"
            @click="applyFilters"
          >
            <v-icon size="21">mdi-magnify</v-icon>
          </v-btn>
        </div>

        <v-menu
          v-if="filterOptions.length"
          v-model="filterMenu"
          location="bottom start"
          :offset="6"
          content-class="rb-toolbar-popup"
        >
          <template #activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="toolbar-dropdown"
              :class="{ 'dropdown-selected': !!selectedFilter }"
            >
              <v-icon size="19" class="dropdown-leading-icon">
                mdi-filter-variant
              </v-icon>

              <span class="dropdown-label">
                {{ selectedFilter?.label || "Filter" }}
              </span>

              <v-icon
                v-if="selectedFilter"
                size="16"
                class="clear-filter-icon"
                @click.stop="clearFilter"
              >
                mdi-close-circle
              </v-icon>

              <v-icon size="18" class="dropdown-chevron">
                mdi-chevron-down
              </v-icon>
            </button>
          </template>

          <v-list
            class="toolbar-menu"
            density="compact"
            elevation="5"
            rounded="lg"
          >
            <div class="menu-heading">Filter By</div>
            <v-divider class="menu-divider" />

            <v-list-item
              v-for="(option, index) in filterOptions"
              :key="index"
              class="menu-option"
              :class="{
                'menu-option-selected': isSameOption(selectedFilter, option),
              }"
              @click="selectFilter(option)"
            >
              <v-list-item-title>
                {{ option.label }}
              </v-list-item-title>

              <template v-if="isSameOption(selectedFilter, option)" #append>
                <v-icon size="17" color="primary"> mdi-check </v-icon>
              </template>
            </v-list-item>

            <template v-if="selectedFilter">
              <v-divider class="my-1 menu-divider" />

              <v-list-item class="menu-option" @click="clearFilter">
                <v-list-item-title class="clear-menu-text">
                  Clear Filter
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>

        <v-menu
          v-if="sortOptions.length"
          v-model="sortMenu"
          location="bottom start"
          :offset="6"
          content-class="rb-toolbar-popup"
        >
          <template #activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="toolbar-dropdown sort-dropdown"
              :class="{ 'dropdown-selected': !!selectedSort }"
            >
              <v-icon size="19" class="dropdown-leading-icon">
                mdi-sort-ascending
              </v-icon>

              <span class="dropdown-label">
                {{ selectedSort?.label || "Sort By" }}
              </span>

              <v-icon
                v-if="selectedSort"
                size="16"
                class="clear-filter-icon"
                @click.stop="clearSort"
              >
                mdi-close-circle
              </v-icon>

              <v-icon size="18" class="dropdown-chevron">
                mdi-chevron-down
              </v-icon>
            </button>
          </template>

          <v-list
            class="toolbar-menu"
            density="compact"
            elevation="5"
            rounded="lg"
          >
            <div class="menu-heading">Sort By</div>
            <v-divider class="menu-divider" />

            <v-list-item
              v-for="(option, index) in sortOptions"
              :key="index"
              class="menu-option"
              :class="{
                'menu-option-selected': isSameOption(selectedSort, option),
              }"
              @click="selectSort(option)"
            >
              <v-list-item-title>
                {{ option.label }}
              </v-list-item-title>

              <template v-if="isSameOption(selectedSort, option)" #append>
                <v-icon size="17" color="primary"> mdi-check </v-icon>
              </template>
            </v-list-item>

            <template v-if="selectedSort">
              <v-divider class="my-1 menu-divider" />

              <v-list-item class="menu-option" @click="clearSort">
                <v-list-item-title class="clear-menu-text">
                  Clear Sort
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>

        <v-menu
          v-if="isDateAllowed()"
          v-model="dateMenu"
          :close-on-content-click="false"
          location="bottom start"
          :offset="6"
          content-class="rb-toolbar-popup"
        >
          <template #activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="toolbar-dropdown date-dropdown"
              :class="{ 'dropdown-selected': !!selectedDate }"
            >
              <v-icon size="19" class="dropdown-leading-icon">
                mdi-calendar-month-outline
              </v-icon>

              <span class="dropdown-label">
                {{ formattedDate || "Select Date" }}
              </span>

              <v-icon
                v-if="selectedDate"
                size="16"
                class="clear-filter-icon"
                @click.stop="clearDate"
              >
                mdi-close-circle
              </v-icon>

              <v-icon size="18" class="dropdown-chevron">
                mdi-chevron-down
              </v-icon>
            </button>
          </template>

          <v-card rounded="lg" elevation="5" class="date-picker-card">
            <v-date-picker
              v-model="selectedDate"
              color="primary"
              @update:model-value="onDateSelect"
            />
          </v-card>
        </v-menu>
      </div>

      <div class="toolbar-right">
        <v-menu
          v-model="exportMenu"
          location="bottom end"
          :offset="6"
          content-class="rb-toolbar-popup"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              class="export-btn"
              prepend-icon="mdi-download-outline"
              append-icon="mdi-chevron-down"
            >
              Export
            </v-btn>
          </template>

          <v-list
            class="toolbar-menu export-menu"
            density="compact"
            elevation="5"
            rounded="lg"
          >
            <v-list-item class="export-option" @click="handleExport('pdf')">
              <template #prepend>
                <v-icon color="error" size="21"> mdi-file-pdf-box </v-icon>
              </template>

              <v-list-item-title> Export as PDF </v-list-item-title>
            </v-list-item>

            <v-list-item class="export-option" @click="handleExport('excel')">
              <template #prepend>
                <v-icon color="success" size="21"> mdi-file-excel </v-icon>
              </template>

              <v-list-item-title> Export as Excel </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          variant="outlined"
          class="reset-btn"
          prepend-icon="mdi-refresh"
          @click="handleReset"
        >
          Reset
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { useUiStore } from "@/store";
import { mapState } from "pinia";

export default {
  name: "ActionToolbar",

  emits: ["filter", "export"],

  props: {
    filterOptions: {
      type: Array,
      default: () => [],
    },

    sortOptions: {
      type: Array,
      default: () => [],
    },

    searchOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      searchMenu: false,
      filterMenu: false,
      sortMenu: false,
      exportMenu: false,
      dateMenu: false,

      selectedSort: null,
      selectedFilter: null,
      selectedDate: null,
      searchValue: "",

      selectedSearchField: this.searchOptions.find(
        (option) => option.default
      ) ||
        this.searchOptions[0] || { label: "Name", value: "name" },

      dateAllowedRoutes: ["Report"],
    };
  },

  computed: {
    ...mapState(useUiStore, {
      loading: "getLoading",
    }),

    availableSearchOptions() {
      return this.searchOptions.length
        ? this.searchOptions
        : [{ label: "Name", value: "name" }];
    },

    formattedDate() {
      if (!this.selectedDate) return "";

      const date = new Date(this.selectedDate);

      if (Number.isNaN(date.getTime())) {
        return String(this.selectedDate);
      }

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },

  methods: {
    isSearchSelected(option) {
      return (
        option?.value !== undefined &&
        this.selectedSearchField?.value === option.value
      );
    },

    selectSearchField(option) {
      this.selectedSearchField = option;
      this.searchMenu = false;

      if (this.searchValue.trim()) {
        this.applyFilters();
      }
    },

    selectFilter(option) {
      this.selectedFilter = option;
      this.filterMenu = false;
      this.applyFilters();
    },

    selectSort(option) {
      this.selectedSort = option;
      this.sortMenu = false;
      this.applyFilters();
    },

    clearFilter() {
      this.selectedFilter = null;
      this.filterMenu = false;
      this.applyFilters();
    },

    clearSort() {
      this.selectedSort = null;
      this.sortMenu = false;
      this.applyFilters();
    },

    clearDate() {
      this.selectedDate = null;
      this.dateMenu = false;
      this.applyFilters();
    },

    isSameOption(selected, option) {
      if (!selected || !option) return false;

      return selected.key === option.key && selected.value === option.value;
    },

    handleReset() {
      this.searchValue = "";
      this.selectedSort = null;
      this.selectedFilter = null;
      this.selectedDate = null;

      this.searchMenu = false;
      this.filterMenu = false;
      this.sortMenu = false;
      this.exportMenu = false;
      this.dateMenu = false;

      this.$emit("filter", {});
    },

    handleSearchClear() {
      this.searchValue = "";
      this.applyFilters();
    },

    onDateSelect(date) {
      if (!date) {
        this.selectedDate = null;
      } else {
        const selected = new Date(date);

        const year = selected.getFullYear();
        const month = String(selected.getMonth() + 1).padStart(2, "0");
        const day = String(selected.getDate()).padStart(2, "0");

        this.selectedDate = `${year}-${month}-${day}`;
      }

      this.dateMenu = false;
      this.applyFilters();
    },

    isDateAllowed() {
      return this.dateAllowedRoutes.includes(this.$route.name);
    },

    applyFilters() {
      const filters = {};

      if (this.selectedSearchField?.value && this.searchValue?.trim()) {
        filters[this.selectedSearchField.value] = this.searchValue.trim();
      }

      if (
        this.selectedFilter?.key &&
        this.selectedFilter?.value !== undefined &&
        this.selectedFilter?.value !== null &&
        this.selectedFilter?.value !== ""
      ) {
        filters[this.selectedFilter.key] = this.selectedFilter.value;
      }

      if (
        this.selectedSort?.key &&
        this.selectedSort?.value !== undefined &&
        this.selectedSort?.value !== null &&
        this.selectedSort?.value !== ""
      ) {
        filters[this.selectedSort.key] = this.selectedSort.value;
      }

      if (this.selectedDate) {
        filters.date = this.selectedDate;
      }

      this.$emit("filter", { filters });
    },

    handleExport(type) {
      this.exportMenu = false;
      this.$emit("export", type);
    },
  },
};
</script>

<style scoped>
.rb-filter-toolbar {
  width: 100%;
  padding: 12px 14px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  /* border: 1px solid rgb(var(--v-theme-rb-border)); */
  /* border-radius: 10px; */
}

.toolbar-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
}

.toolbar-left {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.search-wrapper {
  display: flex;
  align-items: stretch;
  flex: 1 1 350px;
  max-width: 470px;
  min-width: 260px;
  height: 40px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-wrapper:focus-within {
  border-color: #ffb000;
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
}

.search-type-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 0 0 auto;
  width: 148px;
  height: 100%;
  padding: 0 12px;
  background: rgb(var(--v-theme-surface-variant));
  border: 0;
  border-right: 1px solid rgb(var(--v-theme-rb-border));
  color: rgb(var(--v-theme-rb-text));
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.search-type-selector .v-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-rb-muted));
}

.search-type-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
}

.search-input {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.search-input .v-field) {
  min-height: 38px !important;
  height: 38px !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.search-input .v-field__outline) {
  display: none !important;
}

:deep(.search-input .v-field__input) {
  min-height: 38px !important;
  height: 38px !important;
  padding: 0 12px !important;
  color: rgb(var(--v-theme-rb-text)) !important;
  font-size: 13px !important;
  font-weight: 500;
}

:deep(.search-input input::placeholder) {
  color: rgb(var(--v-theme-rb-muted)) !important;
  opacity: 1;
}

:deep(.search-input .v-field__clearable) {
  color: rgb(var(--v-theme-rb-muted)) !important;
}

.search-btn {
  width: 44px !important;
  min-width: 44px !important;
  height: 38px !important;
  padding: 0 !important;
  border-radius: 0 !important;
  background: #ffb000 !important;
  color: #000000 !important;
  box-shadow: none !important;
}

.search-btn:hover,
.search-btn:focus,
.search-btn:active {
  background: #ffb000 !important;
  color: #000000 !important;
}

.search-btn :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

.toolbar-dropdown {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 160px;
  min-width: 160px;
  height: 40px;
  padding: 0 12px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 8px;
  color: rgb(var(--v-theme-rb-text));
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.sort-dropdown {
  width: 180px;
  min-width: 180px;
}

.date-dropdown {
  width: 170px;
  min-width: 170px;
}

.toolbar-dropdown:hover {
  border-color: #ffb000;
  background: rgb(var(--v-theme-rb-sidebar-hover));
}

.dropdown-selected {
  border-color: #ffb000;
  background: rgb(var(--v-theme-rb-sidebar-active));
  color: rgb(var(--v-theme-rb-sidebar-active-text));
}

.dropdown-leading-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-rb-muted));
}

.dropdown-selected .dropdown-leading-icon {
  color: #ffb000;
}

.dropdown-label {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.dropdown-chevron,
.clear-filter-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-rb-muted));
}

.clear-filter-icon:hover {
  color: rgb(var(--v-theme-error));
}

.toolbar-menu {
  min-width: 200px;
  max-width: 280px;
  padding: 6px !important;
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 10px !important;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  overflow: hidden;
}

.search-menu {
  min-width: 240px;
  max-width: 280px;
}

.menu-heading {
  padding: 11px 12px;
  color: rgb(var(--v-theme-rb-text));
  font-size: 13px;
  font-weight: 700;
}

.menu-divider {
  margin-bottom: 5px;
  border-color: rgb(var(--v-theme-rb-border)) !important;
  opacity: 1;
}

.menu-option {
  min-height: 40px !important;
  margin: 3px 0;
  border-radius: 7px !important;
  color: rgb(var(--v-theme-rb-text)) !important;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

:deep(.menu-option .v-list-item-title) {
  color: rgb(var(--v-theme-rb-text)) !important;
  font-size: 12.5px;
  font-weight: 500;
}

:deep(.menu-option .v-list-item__prepend) {
  margin-right: 10px;
}

:deep(.menu-option .v-list-item__overlay) {
  opacity: 0 !important;
}

.menu-option:hover {
  background: rgb(var(--v-theme-rb-sidebar-hover)) !important;
}

.menu-option-selected {
  background: rgb(var(--v-theme-rb-sidebar-active)) !important;
}

:deep(.menu-option-selected .v-list-item-title) {
  color: rgb(var(--v-theme-rb-sidebar-active-text)) !important;
  font-weight: 700 !important;
}

.search-option {
  min-height: 43px !important;
  padding: 0 12px !important;
  background: transparent !important;
}

.search-option:hover {
  background: rgb(var(--v-theme-rb-sidebar-hover)) !important;
}

.search-option-selected,
.search-option-selected:hover {
  background: rgb(var(--v-theme-rb-sidebar-active)) !important;
}

:deep(.search-option .v-list-item-title) {
  color: rgb(var(--v-theme-rb-text)) !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
}

:deep(.search-option-selected .v-list-item-title) {
  color: rgb(var(--v-theme-rb-sidebar-active-text)) !important;
  font-weight: 700 !important;
}

.search-radio {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  border: 1.8px solid rgb(var(--v-theme-rb-muted));
  border-radius: 50%;
  background: transparent;
}

.search-radio-checked {
  border: 2px solid #ffb000;
}

.search-radio-checked span {
  display: block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffb000;
}

.selected-check {
  color: #ffb000 !important;
}

:deep(.search-option .v-list-item__prepend) {
  margin-right: 12px !important;
}

:deep(.search-option .v-list-item__append) {
  margin-left: 12px !important;
}

.clear-menu-text {
  color: rgb(var(--v-theme-error)) !important;
}

.export-btn,
.reset-btn {
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 14px !important;
  border: 1px solid rgb(var(--v-theme-rb-border)) !important;
  border-radius: 8px !important;
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-rb-text)) !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  box-shadow: none !important;
  white-space: nowrap;
}

.export-btn:hover,
.reset-btn:hover {
  border-color: #ffb000 !important;
  background: rgb(var(--v-theme-rb-sidebar-hover)) !important;
}

.export-menu {
  min-width: 220px;
  padding: 6px;
}

.export-option {
  min-height: 44px !important;
  margin: 2px 0;
  border-radius: 7px;
}

.export-option:hover {
  background: rgb(var(--v-theme-rb-sidebar-hover)) !important;
}

:deep(.export-option .v-list-item-title) {
  color: rgb(var(--v-theme-rb-text)) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
}

:deep(.export-option .v-list-item__prepend) {
  margin-right: 12px;
}

:deep(.toolbar-menu .v-list-item--active) {
  background: rgb(var(--v-theme-rb-sidebar-active));
}

.date-picker-card {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border: 1px solid rgb(var(--v-theme-rb-border));
}

.date-picker-card :deep(.v-date-picker) {
  background: rgb(var(--v-theme-surface)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.date-picker-card :deep(.v-date-picker-header),
.date-picker-card :deep(.v-picker-title) {
  color: rgb(var(--v-theme-rb-text)) !important;
}

.date-picker-card :deep(.v-btn--active) {
  background: #ffb000 !important;
  color: #000000 !important;
}

.date-picker-card :deep(.v-btn--active .v-btn__overlay) {
  opacity: 0 !important;
}

@media (max-width: 1200px) {
  .toolbar-main {
    align-items: stretch;
    flex-direction: column;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
  }

  .toolbar-right {
    justify-content: flex-end;
  }

  .search-wrapper {
    max-width: none;
  }
}

@media (max-width: 700px) {
  .rb-filter-toolbar {
    padding: 12px;
  }

  .toolbar-left {
    gap: 10px;
  }

  .search-wrapper {
    flex: 1 1 100%;
    min-width: 0;
  }

  .search-type-selector {
    width: 125px;
    padding: 0 9px;
  }

  .toolbar-dropdown,
  .sort-dropdown,
  .date-dropdown {
    flex: 1 1 calc(50% - 5px);
    width: auto;
    min-width: 145px;
  }

  .toolbar-right {
    justify-content: flex-start;
  }

  .export-btn,
  .reset-btn {
    flex: 1 1 auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-wrapper,
  .toolbar-dropdown,
  .menu-option {
    transition: none;
  }
}
</style>
